import React, { useEffect, useState } from "react";
import { Popup, GenericInput } from "../../../components";

/**
 * @typedef NodeNameDialogProps
 * @property {boolean} isOpen
 * @property {string} title
 * @property {string} confirmLabel
 * @property {string} [initialName]
 * @property {boolean} [isSubmitting]
 * @property {(name: string) => void | Promise<void>} onConfirm
 * @property {() => void} onClose
 */

/**
 * Folder/page name dialog.
 * @param {NodeNameDialogProps} props
 */
export function NodeNameDialog({
	isOpen,
	title,
	confirmLabel,
	initialName = "",
	isSubmitting = false,
	onConfirm,
	onClose,
}) {
	const [name, setName] = useState(initialName);
	const [errorMessage, setErrorMessage] = useState("");

	useEffect(() => {
		if (isOpen) {
			setName(initialName || "");
			setErrorMessage("");
		}
	}, [initialName, isOpen]);

	if (!isOpen) return null;

	const handleConfirm = () => {
		if (isSubmitting) return;
		const trimmed = name.trim();
		if (!trimmed) {
			setErrorMessage("Name is required.");
			return;
		}
		onConfirm(trimmed);
	};

	const handleKeyDown = (event) => {
		if (event.key === "Enter") {
			event.preventDefault();
		}
	};

	return (
		<Popup
			title={title}
			minWidth={400}
			maxWidth={600}
			hasPadding
			onClose={() => {
				if (!isSubmitting) onClose();
			}}
			buttons={[
				{
					type: "primary",
					onClick: handleConfirm,
					isActive: !isSubmitting,
					text: confirmLabel,
				},
				{
					type: "tertiary",
					onClick: () => {
						if (!isSubmitting) onClose();
					},
					isActive: !isSubmitting,
					text: "Cancel",
				},
			]}
		>
			<form onSubmit={(event) => event.preventDefault()}>
				<GenericInput
					type="text"
					label="Name"
					onChange={(event) => {
						setName(event.target.value);
						setErrorMessage("");
					}}
					onKeyDown={handleKeyDown}
					value={name}
					errorMessage={errorMessage}
					placeholder="Eg. Useful Information, Policies, etc."
					isValid={name.trim().length > 0}
					showError={!!errorMessage}
					isRequired
				/>
			</form>
		</Popup>
	);
}
